import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  ClipboardPaste,
  Sparkles,
  Trash2,
  FileCode2,
  Eye,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  listFiles,
  createFile,
  deleteFile,
  loadSamples,
} from "@/lib/api";
import PasteDialog from "@/components/PasteDialog";

export default function LibraryPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const fileInput = useRef(null);
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      setFiles(await listFiles());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setBusy(true);
    try {
      for (const f of selected) {
        const text = await f.text();
        await createFile(f.name, text);
      }
      toast.success(`${selected.length} file(s) parsed & stored`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to parse XML");
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handleSamples = async () => {
    setBusy(true);
    try {
      await loadSamples();
      toast.success("Sample ADAC dataset loaded");
      await refresh();
    } catch {
      toast.error("Could not load samples");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteFile(id);
    toast.success("File removed");
    setFiles((f) => f.filter((x) => x.id !== id));
  };

  const onPasted = async () => {
    await refresh();
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Library</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            {files.length} XML document{files.length === 1 ? "" : "s"} stored
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".xml,text/xml,application/xml"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="file-input"
          />
          <Button
            variant="outline"
            data-testid="load-samples-btn"
            onClick={handleSamples}
            disabled={busy}
            className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Load Sample
          </Button>
          <Button
            variant="outline"
            data-testid="paste-xml-btn"
            onClick={() => setPasteOpen(true)}
            className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
          >
            <ClipboardPaste className="w-4 h-4 mr-2" /> Paste
          </Button>
          <Button
            data-testid="upload-xml-btn"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload XML
          </Button>
        </div>
      </header>

      <div className="p-8">
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : files.length === 0 ? (
          <div
            data-testid="empty-library"
            className="grid-texture border border-zinc-800 rounded-sm py-24 flex flex-col items-center justify-center text-center"
          >
            <FileCode2 className="w-10 h-10 text-zinc-700 mb-4" />
            <h3 className="font-display text-lg font-semibold text-zinc-300">
              No XML documents yet
            </h3>
            <p className="text-sm text-zinc-500 mt-1 max-w-md">
              Upload an ADAC (Asset Design As Constructed) XML file, paste raw
              XML, or load the built-in sample dataset to get started.
            </p>
            <Button
              onClick={handleSamples}
              disabled={busy}
              className="mt-6 rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="empty-load-samples-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Load Sample Dataset
            </Button>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="files-table">
              <thead>
                <tr className="bg-[#101010] border-b border-zinc-800 text-zinc-500 font-mono text-[11px] uppercase tracking-wider">
                  <th className="text-left font-medium px-4 py-3">File name</th>
                  <th className="text-left font-medium px-4 py-3">Root</th>
                  <th className="text-right font-medium px-4 py-3">Assets</th>
                  <th className="text-left font-medium px-4 py-3">Uploaded</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr
                    key={f.id}
                    data-testid={`file-row-${f.id}`}
                    className="border-b border-zinc-900 hover:bg-zinc-900/50 transition-colors duration-75"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileCode2 className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="font-mono text-[13px] text-zinc-200">
                          {f.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      &lt;{f.root_tag}&gt;
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">
                      {f.asset_count}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      {new Date(f.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`view-file-${f.id}`}
                          onClick={() => navigate(`/reader/${f.id}`)}
                          className="h-8 rounded-sm text-zinc-400 hover:text-blue-400 hover:bg-zinc-800"
                        >
                          <Eye className="w-4 h-4 mr-1" /> Read
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`delete-file-${f.id}`}
                          onClick={() => handleDelete(f.id)}
                          className="h-8 w-8 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PasteDialog open={pasteOpen} onOpenChange={setPasteOpen} onSaved={onPasted} />
    </div>
  );
}
